import React from 'react'
import styled, { AnyStyledComponent } from 'styled-components'
import { COLORS } from 'static/constants'
import injectGoogleMaps from 'utils/injectGoogleMaps'
import { useOtaReservation } from 'utils/otaReservation/useOtaReservation'
import OperationPlaceSectionContent from './parts/OperationPlaceSectionContent'
import OperationsGuestSectionContent from './parts/OperationsGuestSectionContent'
import OperationsTourSectionContent from './parts/OperationsTourSectionContent'
import SalesChannelSectionContent from './parts/SalesChannelSectionContent'
import SalesPaymentSectionContent from './parts/SalesPaymentSectionContent'
import SystemSectionContent from './parts/SystemSectionContent'
import OtaReservationHeader from './parts/OtaReservationHeader'
import Panel from 'components/atoms/Panel'
import { IOtaReservationProps } from 'utils/otaReservation/useOtaReservation'

interface IProps {
  props: IOtaReservationProps
  google: any
}
function OtaReservationDetailLayout({ props, google }: IProps) {
  const { otaReservation, editor } = useOtaReservation(props)
  const [isEditing, setIsEditing] = React.useState(false)

  const handleCancel = () => {
    editor.resetEditValues()
    setIsEditing(false)
  }

  const handleSave = async () => {
    const saved = await editor.saveEditValues()
    if (saved) {
      setIsEditing(false)
    }
  }

  return (
    <Panel className="clip" marginType="dashboard">
      <OtaReservationDetailWrapper>
        <OtaReservationHeader
          otaReservation={otaReservation}
          isEditing={isEditing}
          isSaving={editor.isSaving}
          canEdit={editor.canEdit}
          onStartEdit={() => setIsEditing(true)}
          onSave={handleSave}
          onCancel={handleCancel}
        />
        <Section>
          <SectionTitle>
            <div className="mainTitle">OPERATIONS</div>
            <div className="subTitle">オペレーションで使う情報</div>
          </SectionTitle>
          <OperationsTourSectionContent
            otaReservation={otaReservation}
            isEditing={isEditing}
            editor={editor.tour}
          />
          <OperationsGuestSectionContent
            otaReservation={otaReservation}
            isEditing={isEditing}
            editor={editor.guest}
          />
          <OperationPlaceSectionContent
            otaReservation={otaReservation}
            isEditing={isEditing}
            editor={editor.place}
            google={google}
          />
        </Section>
        <Section>
          <SectionTitle>
            <div className="mainTitle">SALES & BILLING</div>
            <div className="subTitle">チャネル・価格まわりの会計情報</div>
          </SectionTitle>
          <SectionGroup>
            <SalesChannelSectionContent otaReservation={otaReservation} />
            <SalesPaymentSectionContent otaReservation={otaReservation} />
          </SectionGroup>
        </Section>
        <Section className="no-marginBottom">
          <SectionTitle>
            <div className="mainTitle">SYSTEM</div>
            <div className="subTitle">開発・データ連携用の基本情報</div>
          </SectionTitle>
          <SectionGroup>
            <SystemSectionContent otaReservation={otaReservation} />
          </SectionGroup>
        </Section>
      </OtaReservationDetailWrapper>
    </Panel>
  )
}

export default injectGoogleMaps(OtaReservationDetailLayout)

// --- このコンポーネント内でのみ使用 ---
const OtaReservationDetailWrapper: AnyStyledComponent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const SectionTitle: AnyStyledComponent = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  .mainTitle {
    font-size: 14px;
    font-weight: bold;
    color: ${COLORS.primary};
    border-left: 3px solid ${COLORS.primary};
    padding: 0 8px;
    margin-right: 16px;
  }

  .subTitle {
    font-size: 14px;
    color: ${COLORS.subText};
  }
`

const Section: AnyStyledComponent = styled.div`
  margin-bottom: 24px;
  &.no-marginBottom {
    margin-bottom: 0;
  }
`

const SectionGroup: AnyStyledComponent = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`

// --- パネル内パネル  ---
export const SectionContent: AnyStyledComponent = styled.div`
  width: 100%;
  height: min-content;
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid ${COLORS.divider2};
`

export const ContentHeader: AnyStyledComponent = styled.div`
  font-size: 14px;
  font-weight: bold;
  background-color: ${COLORS.lightGray};
  padding: 8px 16px;
  border-bottom: 1px solid ${COLORS.divider2};
`

export const ContentBody: AnyStyledComponent = styled.div`
  padding: 12px 16px 16px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  &.single-column {
    grid-template-columns: 1fr;
  }

  .unavailable-message {
    color: ${COLORS.subText};
    font-size: 13px;
  }

  .withBorder {
    padding-top: 16px;
    border-top: 1px solid ${COLORS.divider2};
  }
`

// --- 各パネルのデータ表示 ---
export const RowSection: AnyStyledComponent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 50%;
  font-size: 13px;
`

export const RowSectionTitle: AnyStyledComponent = styled.div`
  font-weight: bold;

  .description {
    font-weight: normal;
    font-size: 11px;
    color: ${COLORS.subText};
  }
`
